/**
 * WCAG 2.1 relative luminance and contrast ratio (piece 1), and the ink a
 * coloured fill takes (decision 1290). 🔨 The arithmetic lives here and not in
 * the web, so the token check, the harness and the tree's filled rows read one
 * formula. 🔨 Nothing here reads a stylesheet: the web resolves a token's value
 * and hands the resolved colour in, because this package touches no DOM.
 */

export type Rgb = readonly [number, number, number];

/** A `#RGB` or `#RRGGBB` colour as its three channels; anything else is refused by name. */
export function hexToRgb(hex: string): [number, number, number] {
  const h = hex.trim().replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) throw new Error(`That is not a hex color: ${hex}.`);
  return [parseInt(full.slice(0, 2), 16), parseInt(full.slice(2, 4), 16), parseInt(full.slice(4, 6), 16)];
}

const RGB_FUNCTION = /^rgba?\(\s*(\d{1,3})[\s,]+(\d{1,3})[\s,]+(\d{1,3})/i;

/**
 * A colour as a stylesheet resolves it — a hex from the token file, or the
 * `rgb()` form a browser reports — as its three channels, or null where the
 * value is not a colour this can read (an empty string from a page with no
 * stylesheet loaded, a keyword, a gradient).
 */
export function parseColor(value: string | null | undefined): Rgb | null {
  const text = (value ?? '').trim();
  if (text.startsWith('#')) {
    try {
      return hexToRgb(text);
    } catch {
      return null;
    }
  }
  const match = RGB_FUNCTION.exec(text);
  if (!match) return null;
  const channels = [Number(match[1]), Number(match[2]), Number(match[3])] as const;
  return channels.every((c) => c >= 0 && c <= 255) ? channels : null;
}

function channel(c: number): number {
  const s = c / 255;
  return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

function luminanceOf(rgb: Rgb): number {
  return 0.2126 * channel(rgb[0]) + 0.7152 * channel(rgb[1]) + 0.0722 * channel(rgb[2]);
}

export function relativeLuminance(hex: string): number {
  return luminanceOf(hexToRgb(hex));
}

function ratioOf(a: Rgb, b: Rgb): number {
  const l1 = luminanceOf(a);
  const l2 = luminanceOf(b);
  const [hi, lo] = l1 >= l2 ? [l1, l2] : [l2, l1];
  return (hi + 0.05) / (lo + 0.05);
}

export function contrastRatio(foreground: string, background: string): number {
  return ratioOf(hexToRgb(foreground), hexToRgb(background));
}

/** Which of the two text tokens a fill takes. */
export type FillInk = 'light' | 'dark';

/**
 * 🔒 The ink a coloured fill takes (decision 1290): whichever of the light and
 * the dark text colour reads at the higher contrast against it, the light one
 * on a tie. Each argument is a colour already resolved from its token. Null
 * where any of the three cannot be read, so a caller decides what an unloaded
 * stylesheet means rather than this guessing.
 */
export function inkOn(fill: string | null | undefined, light: string | null | undefined, dark: string | null | undefined): FillInk | null {
  const [f, l, d] = [parseColor(fill), parseColor(light), parseColor(dark)];
  if (!f || !l || !d) return null;
  return ratioOf(l, f) >= ratioOf(d, f) ? 'light' : 'dark';
}

/** The custom property a `var(--name)` expression names, or null where the expression is not one. */
export function cssVariableName(expression: string | null | undefined): string | null {
  const match = /^\s*var\(\s*(--[A-Za-z0-9-]+)\s*(?:,[^)]*)?\)\s*$/.exec(expression ?? '');
  return match ? match[1]! : null;
}
